import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useAppAuth } from '../components/AuthContext.jsx';

// KPI systému (JCH-KPI): devět ukazatelů počítá API z dat Drillu (GET /core/kpi).
// Čas JR zapisuje JR sám (součet za týden).

const monday = (d = new Date()) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

export default function KPI() {
  const { user } = useAppAuth();
  const [cohort, setCohort] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [hours, setHours] = useState('');
  const [saved, setSaved] = useState('');
  const [category, setCategory] = useState('administration');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/core/kpi', { params: cohort ? { cohort } : {} });
      setData(res.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'KPI se nepodařilo načíst.');
    }
  }, [cohort]);

  useEffect(() => {
    load();
  }, [load]);

  const saveHours = async (e) => {
    e.preventDefault();
    try {
      await api.put('/core/kpi/jr-time', {
        week_start: monday(),
        hours: Number(String(hours).replace(',', '.')),
        category_code: category,
      });
      setSaved('Uloženo.');
      setHours('');
      load();
    } catch (err) {
      setSaved(err.response?.data?.error || 'Uložení se nepovedlo.');
    }
  };

  const kpis = data?.kpis || [];
  const cohorts = data?.cohorts || [];
  const categories = data?.jr_time_categories || [];

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">KPI systému</h1>
          <p className="dashboard__lead">
            Ukazatele, podle kterých se pozná, že systém funguje. Devět ukazatelů. S JCH je probíráte
            na měsíční půlhodině po {data?.targets.readyDay ?? 'uzavření čísel'} – report za vlny, úrovně a stav systému, nad stejnou obrazovkou.
          </p>
        </div>
        <span className="chip chip--grn">
          Cíle a modelové předpoklady, nikoli naměřené výsledky
        </span>
      </div>

      {error && <p className="form__error" role="alert">{error}</p>}

      <div className="kpi__cohorts" role="group" aria-label="Kohorta">
        {[null, ...cohorts].map((label) => (
          <button
            key={label || 'all'}
            type="button"
            className={`button button--chip ${cohort === label ? 'button--chip--on' : ''}`}
            onClick={() => setCohort(label)}
            aria-pressed={cohort === label}
          >
            {label || 'Všechny od 1. skupiny'}
          </button>
        ))}
        <span className="kpi__cohort-hint">
          {data ? `${data.maklers} makléřů v měření` : 'Načítám…'}
        </span>
      </div>

      {/* KPI table */}
      <div className="kpi" role="table" aria-label="KPI systému">
        <div className="kpi__row kpi__row--head">
          <span className="kpi__head">KPI</span>
          <span className="kpi__head kpi__head--what">Co měří</span>
          <span className="kpi__head">Hodnota</span>
          <span className="kpi__head">Cíl</span>
          <span className="kpi__head">Z modelu · poznámka</span>
        </div>
        {kpis.map((k) => (
          <div key={k.key} className="kpi__row">
            <span className="kpi__cell kpi__cell--strong">{k.kpi}</span>
            <span className="kpi__cell">{k.what}</span>
            <span className="kpi__cell">{k.value}</span>
            <span className="kpi__cell">{k.target}</span>
            <span className="kpi__cell">{k.note}</span>
          </div>
        ))}
      </div>

      <p className="kpi__note">
        Vedle ukazatelů: krátká anketa makléřů po 3 a po 6 měsících u každé
        skupiny. Vyhodnocujete ji s JR na měsíční půlhodině.
      </p>

      {user?.role === 'JR' && (
        <form className="card" onSubmit={saveHours} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>Čas JR tento týden (h)</strong>
          <label>
            Kategorie
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="" disabled>Vyberte kategorii</option>
              {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </label>
          <input
            className="input"
            inputMode="decimal"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="např. 24,5"
            aria-label="Hodiny za týden"
            style={{ width: 120 }}
          />
          <button type="submit" className="button" disabled={!hours || !categories.some((c) => c.code === category)}>Uložit</button>
          <small>Ukládá se součet za vybranou kategorii a týden; ostatní kategorie se nesmažou.</small>
          {saved && <span role="status">{saved}</span>}
        </form>
      )}

      {/* First-group test panel */}
      <article className="card">
        <div className="kpi__test-head">
          <h2 className="card__title">Referenční kritéria testu skupiny</h2>
          <span className="chip chip--spot">modelové cíle</span>
        </div>
        <dl className="kpi__test">
          <div>
            <dt>Zápis do Drillu</dt>
            <dd>Kritériem testu je zápis aspoň {data?.targets.entryDaysPercent ?? '—'} % dnů. Cílem je zápis každý den.</dd>
          </div>
          <div>
            <dt>Čas JR</dt>
            <dd>
              Měří se od startu: odhad z kalendáře a týdenní součet od JR. Na
              začátku se podle něj nic nemění. Modelový cíl je pod {data?.targets.jrWeeklyHours ?? '—'} h týdně
              i po 8 měsících od skutečného začátku skupiny.
            </dd>
          </div>
          <div>
            <dt>Plnění plánu</dt>
            <dd>Kritériem testu není. Sleduje se v běžném ukazateli nahoře.</dd>
          </div>
          <div>
            <dt>Vyhodnocení</dt>
            <dd>Průběžně, ty a JR. Pevný termín se nestanovuje.</dd>
          </div>
        </dl>
        <p className="card__hint">
          Konkrétní datum startu ani vyhodnocení skupiny zde není evidováno.
          Naměřené hodnoty jsou v tabulce výše pro vybranou kohortu.
        </p>
      </article>

      <div className="kpi__callout">
        <span>
          Kohorta je měsíc začátku kurzu RX Akademie (datum nástupu v Drillu) a
          už se nemění. Porovnávají se kohorty, ne jednotliví makléři.
          Ukazatele se měří až od první skupiny.
        </span>
      </div>
    </section>
  );
}
